import React, { useState, useEffect } from "react";
import {
    Box,
    Container,
    Typography,
    TextField,
    Button,
    Paper,
    Divider,
    Grid,
    useTheme,
    useMediaQuery,
    Autocomplete,
    Chip,
    CircularProgress,
    Avatar,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Collapse,
    FormControlLabel,
    Checkbox,
    Skeleton,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { getAllSkills, searchSkillsApi } from "../../Api/SkillsApi";
import { getActiveLocations, getJobs } from "../../Api/JobsServiceApi";
import { searchAllCandidates, getCandidateResume, downloadCandidateResume } from "../../Api/CandidateApi";
import FilterListIcon from "@mui/icons-material/FilterList";
import AdvancedSearchFilters from "../../Components/AdvancedSearchFilters";

const SearchCandidate = () => {
    const navigate = useNavigate();
    const [searchData, setSearchData] = useState({
        search: "",
        location: "",
    });

    const [advancedFiltersOpen, setAdvancedFiltersOpen] = useState(false);
    const [advancedFilters, setAdvancedFilters] = useState({
        jobExperience: [],
        jobType: [],
        education: [],
        noticePeriod: [],
    });

    const handleCheckboxChange = (category, value) => {
        setAdvancedFilters((prev) => {
            const currentValues = prev[category];
            const newValues = currentValues.includes(value) ? currentValues.filter((item) => item !== value) : [...currentValues, value];
            return {
                ...prev,
                [category]: newValues,
            };
        });
    };

    const handleRemoveFilter = (category, value) => {
        setAdvancedFilters(prev => ({
            ...prev,
            [category]: prev[category].filter(v => v !== value)
        }));
    };

    const handleClearFilters = () => {
        setAdvancedFilters({
            jobExperience: [],
            jobType: [],
            education: [],
            noticePeriod: [],
        });
    };

    const hasActiveFilters = Object.values(advancedFilters).some(arr => arr.length > 0);

    const jobExperienceOptions = ["1 year", "2 year", "5 year", "10 year"];
    const jobTypeOptions = ["Fulltime", "Temporary/Contract", "Intern", "Part Time", "Seasonal"];
    const educationOptions = ["Graduation", "PG (Post Graduation)", "PhD", "Diploma"];
    const noticePeriodOptions = ["Immediate", "10 Days", "1 Month", "3 Months"];

    // Skills Autocomplete states
    const [allSkills, setAllSkills] = useState([]);
    const [selectedSkills, setSelectedSkills] = useState([]);
    const [skillsInputValue, setSkillsInputValue] = useState("");
    const [loadingSkills, setLoadingSkills] = useState(false);

    // Locations Autocomplete states
    const [locationsList, setLocationsList] = useState([]);

    // Matched Candidates states
    const [matchedCandidates, setMatchedCandidates] = useState([]);
    const [loadingMatches, setLoadingMatches] = useState(false);
    const [selectedCandidate, setSelectedCandidate] = useState(null);
    const [resumeLoading, setResumeLoading] = useState(false);

    const theme = useTheme();
    const mdUp = useMediaQuery(theme.breakpoints.up("md"));

    // Fetch matched candidates on load
    useEffect(() => {
        const loadMatchedCandidates = async () => {
            setLoadingMatches(true);
            try {
                // Fetch active job postings
                const jobsRes = await getJobs();
                const activeJobs = jobsRes?.data || jobsRes?.jobs || [];

                // Fetch all candidates
                const candidatesRes = await searchAllCandidates({ limit: 100 });
                const rawList = candidatesRes?.jobApplications || candidatesRes?.data || [];

                // Map raw items into candidate records
                const parsedCandidates = rawList.map(item => {
                    const isApplication = !!item.candidate;
                    const candidate = isApplication ? item.candidate : item;
                    return {
                        id: item.id,
                        userId: isApplication ? item.userId : item.id,
                        name: candidate?.name || '',
                        email: candidate?.email || '',
                        avatarUrl: candidate?.personalDetails?.profile || candidate?.avatarUrl || '',
                        location: candidate?.preferredLocations?.[0]?.location?.name || '',
                        skills: candidate?.userSkills?.map(us => us.skill?.name || us.skill) || candidate?.skills || [],
                        experience: candidate?.personalDetails?.experience || candidate?.experience || null,
                        phone: candidate?.mobile || candidate?.personalDetails?.phone || candidate?.phone || '',
                        jobTitle: isApplication ? item.job?.title : (candidate?.personalDetails?.designation || candidate?.personalDetails?.jobTitle || ''),
                        _raw: item
                    };
                });

                // Compute match score for each candidate against each active job post
                const matches = [];
                parsedCandidates.forEach(cand => {
                    const candSkills = cand.skills.map(s => {
                        const sName = typeof s === 'string' ? s : s?.name || '';
                        return sName.toLowerCase().trim();
                    }).filter(Boolean);

                    if (candSkills.length === 0) return;

                    const candMatchedJobs = [];
                    let maxPercent = 0;

                    activeJobs.forEach(job => {
                        const jobSkills = (job.skills || []).map(s => s.toLowerCase().trim()).filter(Boolean);
                        if (jobSkills.length === 0) return;

                        // Find matching skills
                        const overlap = candSkills.filter(s => jobSkills.includes(s));
                        if (overlap.length > 0) {
                            const percent = (overlap.length / jobSkills.length) * 100;
                            const rounded = Math.round(percent);
                            if (rounded > maxPercent) maxPercent = rounded;

                            candMatchedJobs.push({
                                id: job.id,
                                title: job.title,
                                percent: rounded
                            });
                        }
                    });

                    if (candMatchedJobs.length > 0) {
                        candMatchedJobs.sort((a, b) => b.percent - a.percent);
                        matches.push({
                            ...cand,
                            matchedJobs: candMatchedJobs,
                            maxPercent: maxPercent
                        });
                    }
                });

                // Sort by highest match score descending
                matches.sort((a, b) => b.maxPercent - a.maxPercent);
                setMatchedCandidates(matches);
            } catch (err) {
                console.error("Error loading matched candidates:", err);
            } finally {
                setLoadingMatches(false);
            }
        };
        loadMatchedCandidates();
    }, []);

    // Fetch initial skills and locations on load
    useEffect(() => {
        const loadInitialSkillsAndLocations = async () => {
            try {
                const res = await getAllSkills(100);
                if (res?.success) {
                    setAllSkills(res.data || []);
                }
            } catch (error) {
                console.error("Failed to load initial skills:", error);
            }
            try {
                const res = await getActiveLocations();
                if (res?.success && res?.locations) {
                    setLocationsList(res.locations);
                }
            } catch (error) {
                console.error("Failed to load locations:", error);
            }
        };
        loadInitialSkillsAndLocations();
    }, []);

    // Debounced search for skills
    useEffect(() => {
        const handler = setTimeout(async () => {
            if (skillsInputValue.trim() !== '') {
                setLoadingSkills(true);
                try {
                    const res = await searchSkillsApi(skillsInputValue);
                    if (res?.success) {
                        const results = res.data || [];
                        setAllSkills(prev => {
                            const merged = [...prev];
                            results.forEach(item => {
                                if (!merged.some(m => m.id === item.id)) {
                                    merged.push(item);
                                }
                            });
                            return merged;
                        });
                    }
                } catch (error) {
                    console.error("Failed to search skills:", error);
                } finally {
                    setLoadingSkills(false);
                }
            }
        }, 500);
        return () => clearTimeout(handler);
    }, [skillsInputValue]);

    const handleInputChange = (field) => (e) =>
        setSearchData((s) => ({ ...s, [field]: e.target.value }));

    const handleSearch = () => {
        const queryParams = new URLSearchParams();
        if (searchData.search.trim()) {
            queryParams.append("search", searchData.search.trim());
        }

        const skillsString = selectedSkills
            .map(s => typeof s === 'string' ? s : s.name || s.skillName || '')
            .filter(Boolean)
            .join(",");
        if (skillsString) queryParams.append("skills", skillsString);

        if (searchData.location) queryParams.append("location", searchData.location);

        if (advancedFilters.jobExperience.length > 0) {
            queryParams.append("experience", advancedFilters.jobExperience.join(","));
        }
        if (advancedFilters.noticePeriod.length > 0) {
            queryParams.append("noticePeriod", advancedFilters.noticePeriod.join(","));
        }
        if (advancedFilters.education.length > 0) {
            queryParams.append("education", advancedFilters.education.join(","));
        }
        if (advancedFilters.jobType.length > 0) {
            queryParams.append("jobType", advancedFilters.jobType.join(","));
        }

        navigate(`/search-results?${queryParams.toString()}`);
    };

    const handleCandidateSelect = (candidate) => {
        setSelectedCandidate(candidate);
    };

    const handleViewResume = async () => {
        if (!selectedCandidate) return;
        setResumeLoading(true);
        try {
            const res = await getCandidateResume(selectedCandidate.userId || selectedCandidate.id);
            const url = res?.data?.resumeUrl || res?.resumeUrl;
            if (url) {
                window.open(url, '_blank');
            } else {
                window.alert('No resume available for this candidate.');
            }
        } catch (error) {
            console.error('Failed to fetch candidate resume:', error);
            window.alert('Failed to fetch candidate resume.');
        } finally {
            setResumeLoading(false);
        }
    };

    const handleDownloadResume = () => {
        if (!selectedCandidate) return;
        downloadCandidateResume(selectedCandidate.userId || selectedCandidate.id);
    };

    return (
        <Box sx={{ minHeight: '100%', backgroundColor: '#F8F9FA', pb: 8 }}>
            {/* Page Header */}
            <Box sx={{ px: { xs: 2, md: 4 }, pt: { xs: 3, md: 4 }, pb: 2, maxWidth: '1400px', mx: 'auto' }}>
                <Typography variant="h3" sx={{ fontWeight: 700, mb: 3 }}>
                    Candidate Search
                </Typography>
            </Box>

            {/* Search Card */}
            <Container sx={{ mb: 4, px: { xs: 1, md: 0 }, width: '90%' }}>
                <Paper elevation={0} sx={{ borderRadius: '12px', overflow: 'visible', p: { xs: 2, md: 0 }, backgroundColor: '#FFFFFF', border: '1px solid #E5E7EB' }}>
                    {/* Search bar area */}
                    <Grid container alignItems="center" spacing={0} sx={{ backgroundColor: "#fff", borderRadius: 3, p: { xs: 1.5, md: 2.5 }, boxShadow: "none" }}>
                        {/* NAME OR JOB TITLE */}
                        <Grid size={{ xs: 12, md: 3.5 }} sx={{ pr: { md: 2 } }}>
                            <Typography variant="subtitle2" sx={{ color: '#9CA3AF', fontWeight: 500, mb: 1, fontSize: '0.6875rem', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                                NAME OR JOB TITLE
                            </Typography>
                            <TextField
                                fullWidth placeholder="Eg: John or UI Designer" size="small" value={searchData.search}
                                onChange={handleInputChange("search")} variant="outlined"
                                InputProps={{
                                    sx: {
                                        backgroundColor: "#fff", width: '100%',
                                        boxShadow: "none",
                                    },
                                }}
                            />
                        </Grid>

                        {/* Vertical divider (only show on mdUp) */}
                        {mdUp && (
                            <Grid size={{ md: "auto" }} sx={{ display: { xs: "none", md: "block" } }}>
                                <Divider
                                    orientation="vertical"
                                    flexItem
                                    sx={{ height: 56, borderColor: "#E6E6E6" }}
                                />
                            </Grid>
                        )}

                        {/* SKILLS */}
                        <Grid size={{ xs: 12, md: 3.5 }} sx={{ px: { md: 2 }, pb: { xs: 1.5, md: 0 }, }} >
                            <Typography variant="subtitle2" sx={{ color: '#9CA3AF', fontWeight: 500, mb: 1, fontSize: '0.6875rem', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                                SKILLS
                            </Typography>
                            <Autocomplete
                                multiple
                                freeSolo
                                id="skills-autocomplete"
                                options={allSkills}
                                getOptionLabel={(option) => {
                                    if (typeof option === "string") return option;
                                    return option.name || option.skillName || "";
                                }}
                                value={selectedSkills}
                                onChange={(event, newValue) => {
                                    setSelectedSkills(newValue);
                                }}
                                inputValue={skillsInputValue}
                                onInputChange={(event, newInputValue) => {
                                    setSkillsInputValue(newInputValue);
                                }}
                                loading={loadingSkills}
                                isOptionEqualToValue={(option, value) => {
                                    const oName = typeof option === "string" ? option : option.name || option.skillName || "";
                                    const vName = typeof value === "string" ? value : value.name || value.skillName || "";
                                    return oName.toLowerCase() === vName.toLowerCase();
                                }}
                                renderTags={(tagValue, getTagProps) =>
                                    tagValue.map((option, index) => {
                                        const { key, ...tagProps } = getTagProps({ index });
                                        return (
                                            <Chip
                                                key={key || index}
                                                label={typeof option === "string" ? option : option.name || option.skillName}
                                                {...tagProps}
                                                sx={{
                                                    borderRadius: "50px", // cylinder/capsule shape!
                                                    backgroundColor: "#e0d4f7",
                                                    color: "#6B31C4",
                                                    fontWeight: 500,
                                                    fontSize: "0.82rem",
                                                    border: "1px solid #6B31C4",
                                                    height: 24,
                                                    "& .MuiChip-deleteIcon": {
                                                        color: "#6B31C4",
                                                        fontSize: "14px",
                                                        "&:hover": {
                                                            color: "#5a29a5",
                                                        }
                                                    }
                                                }}
                                            />
                                        );
                                    })
                                }
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        size="small"
                                        placeholder={selectedSkills.length === 0 ? "Eg: Interaction, UX design" : ""}
                                        variant="outlined"
                                        onKeyDown={(event) => {
                                            if (event.key === ',') {
                                                event.preventDefault();
                                                const value = skillsInputValue.trim();
                                                if (value) {
                                                    const exists = selectedSkills.some(s => {
                                                        const name = typeof s === 'string' ? s : s.name || s.skillName;
                                                        return name.toLowerCase() === value.toLowerCase();
                                                    });
                                                    if (!exists) {
                                                        setSelectedSkills(prev => [...prev, value]);
                                                    }
                                                    setSkillsInputValue('');
                                                }
                                            }
                                        }}
                                        InputProps={{
                                            ...params.InputProps,
                                            endAdornment: (
                                                <>
                                                    {loadingSkills ? <CircularProgress color="inherit" size={20} /> : null}
                                                    {params.InputProps.endAdornment}
                                                </>
                                            ),
                                            sx: {
                                                borderRadius: 1,
                                                backgroundColor: "#fff",
                                                minHeight: "40px",
                                                py: 0.5,
                                                "& .MuiOutlinedInput-notchedOutline": {
                                                    borderColor: "#cbd5e1"
                                                },
                                                "&:hover .MuiOutlinedInput-notchedOutline": {
                                                    borderColor: "#94a3b8"
                                                },
                                                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                                    borderColor: "#6B31C4"
                                                }
                                            }
                                        }}
                                    />
                                )}
                                sx={{
                                    width: "100%",
                                    "& .MuiAutocomplete-inputRoot": {
                                        padding: "4px 8px",
                                    }
                                }}
                            />
                        </Grid>

                        {mdUp && (
                            <Grid size={{ md: "auto" }} sx={{ display: { xs: "none", md: "block" } }}>
                                <Divider orientation="vertical" flexItem sx={{ height: 56, borderColor: "#E6E6E6" }} />
                            </Grid>
                        )}

                        {/* LOCATION */}
                        <Grid size={{ xs: 12, md: 3 }} sx={{ px: { md: 2 }, pb: { xs: 1.5, md: 0 } }}  >
                            <Typography variant="subtitle2" sx={{ color: '#9CA3AF', fontWeight: 500, mb: 1, fontSize: '0.6875rem', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                                LOCATION
                            </Typography>
                            <Autocomplete
                                freeSolo
                                id="location-autocomplete"
                                options={locationsList.map(l => l.name || l)}
                                value={searchData.location}
                                onChange={(event, newValue) => {
                                    setSearchData(prev => ({ ...prev, location: newValue || "" }));
                                }}
                                onInputChange={(event, newInputValue) => {
                                    setSearchData(prev => ({ ...prev, location: newInputValue || "" }));
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        placeholder="City, State, or ZIP"
                                        size="small"
                                        variant="outlined"
                                        InputProps={{
                                            ...params.InputProps,
                                            sx: {
                                                backgroundColor: "#fff",
                                                width: '100%',
                                            }
                                        }}
                                    />
                                )}
                            />
                        </Grid>
                        {/* {mdUp && (
                            <Grid item md="auto" sx={{ display: { xs: "none", md: "block" } }}>
                                <Divider orientation="vertical" flexItem sx={{ height: 56, borderColor: "#E6E6E6" }} />
                            </Grid>
                        )} */}
                        {mdUp && (
                            <Grid size={{ md: "auto" }} sx={{ display: { xs: "none", md: "block" } }}>
                                <Divider orientation="vertical" flexItem sx={{ height: 56, borderColor: "#E6E6E6" }} />
                            </Grid>
                        )}

                        {/* Search button */}
                        <Grid size={{ xs: 12, md: 1.5 }} sx={{ pl: { md: 2 } }}>
                            {mdUp && (
                                <Typography variant="subtitle2" sx={{ visibility: 'hidden', mb: 1, fontSize: '0.6875rem', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                                    SEARCH
                                </Typography>
                            )}
                            <Button
                                onClick={handleSearch}
                                disableFocusRipple disableRipple variant="contained" fullWidth={!mdUp}
                                sx={{
                                    px: 3, py: 1.25, width: '100%'
                                }}
                            >
                                Search
                            </Button>
                        </Grid>
                    </Grid>

                    {/* Advanced Filters Collapsible Section */}
                    <Collapse in={advancedFiltersOpen}>
                        <Box sx={{ px: { xs: 2, md: 3 }, pb: 2.5, pt: 2, borderTop: '1px solid #F3F4F6', mt: 1 }}>
                            <Grid container spacing={3}>
                                {/* Experience */}
                                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                    <Typography sx={{ fontSize: '0.6875rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#9CA3AF', mb: 1.5, display: 'block' }}>
                                        Experience
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                                        {jobExperienceOptions.map(opt => (
                                            <FormControlLabel
                                                key={opt}
                                                control={<Checkbox size="small" checked={advancedFilters.jobExperience.includes(opt)} onChange={() => handleCheckboxChange('jobExperience', opt)} sx={{ color: '#D1D5DB', '&.Mui-checked': { color: '#6B31C4' } }} />}
                                                label={<Typography sx={{ fontSize: '0.8125rem', color: '#374151', fontWeight: 500 }}>{opt}</Typography>}
                                                sx={{ m: 0 }}
                                            />
                                        ))}
                                    </Box>
                                </Grid>
                                {/* Job Type */}
                                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                    <Typography sx={{ fontSize: '0.6875rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#9CA3AF', mb: 1.5, display: 'block' }}>
                                        Job Type
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                                        {jobTypeOptions.map(opt => (
                                            <FormControlLabel
                                                key={opt}
                                                control={<Checkbox size="small" checked={advancedFilters.jobType.includes(opt)} onChange={() => handleCheckboxChange('jobType', opt)} sx={{ color: '#D1D5DB', '&.Mui-checked': { color: '#6B31C4' } }} />}
                                                label={<Typography sx={{ fontSize: '0.8125rem', color: '#374151', fontWeight: 500 }}>{opt}</Typography>}
                                                sx={{ m: 0 }}
                                            />
                                        ))}
                                    </Box>
                                </Grid>
                                {/* Education */}
                                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                    <Typography sx={{ fontSize: '0.6875rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#9CA3AF', mb: 1.5, display: 'block' }}>
                                        Education
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                                        {educationOptions.map(opt => (
                                            <FormControlLabel
                                                key={opt}
                                                control={<Checkbox size="small" checked={advancedFilters.education.includes(opt)} onChange={() => handleCheckboxChange('education', opt)} sx={{ color: '#D1D5DB', '&.Mui-checked': { color: '#6B31C4' } }} />}
                                                label={<Typography sx={{ fontSize: '0.8125rem', color: '#374151', fontWeight: 500 }}>{opt}</Typography>}
                                                sx={{ m: 0 }}
                                            />
                                        ))}
                                    </Box>
                                </Grid>
                                {/* Notice Period */}
                                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                    <Typography sx={{ fontSize: '0.6875rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#9CA3AF', mb: 1.5, display: 'block' }}>
                                        Notice Period
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                                        {noticePeriodOptions.map(opt => (
                                            <FormControlLabel
                                                key={opt}
                                                control={<Checkbox size="small" checked={advancedFilters.noticePeriod.includes(opt)} onChange={() => handleCheckboxChange('noticePeriod', opt)} sx={{ color: '#D1D5DB', '&.Mui-checked': { color: '#6B31C4' } }} />}
                                                label={<Typography sx={{ fontSize: '0.8125rem', color: '#374151', fontWeight: 500 }}>{opt}</Typography>}
                                                sx={{ m: 0 }}
                                            />
                                        ))}
                                    </Box>
                                </Grid>
                            </Grid>
                        </Box>
                    </Collapse>

                    {/* Advanced Filters Footer */}
                    <Box sx={{ px: { xs: 2, md: 2.5 }, pb: 2, pt: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderTop: '1px solid #F3F4F6', flexWrap: 'wrap', gap: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                            <Button 
                                variant="text" 
                                startIcon={<FilterListIcon />}
                                onClick={() => setAdvancedFiltersOpen(!advancedFiltersOpen)}
                                sx={{ color: '#6B31C4', textTransform: 'none', fontWeight: 500, fontSize: '0.8125rem', p: 0.5 }}
                            >
                                {advancedFiltersOpen ? 'Hide Advanced Filters' : 'Advanced Filters'}
                            </Button>
                            
                            {/* Selected Chips */}
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {Object.entries(advancedFilters).map(([key, values]) => {
                                    if (!Array.isArray(values) || values.length === 0) return null;
                                    return values.map(val => (
                                        <Chip
                                            key={`${key}-${val}`}
                                            label={val}
                                            size="small"
                                            onDelete={() => handleRemoveFilter(key, val)}
                                            sx={{ backgroundColor: '#F3F4F6', color: '#374151', fontSize: '0.75rem', height: 24 }}
                                        />
                                    ));
                                })}
                            </Box>
                        </Box>
                        
                        {hasActiveFilters && (
                            <Button 
                                variant="text" 
                                size="small"
                                onClick={handleClearFilters}
                                sx={{ color: '#EF4444', textTransform: 'none', fontSize: '0.8125rem' }}
                            >
                                Clear Filters
                            </Button>
                        )}
                    </Box>
                </Paper>

                <Box sx={{ mt: 6 }}>
                    <Typography variant="h4" sx={{ fontWeight: 600, color: '#1A1A2E', mb: 3 }}>
                        Top Matches for Your Job Posts
                    </Typography>

                    {loadingMatches ? (
                        <Grid container spacing={3}>
                            {[1, 2, 3].map((item) => (
                                <Grid size={{ xs: 12, sm: 6, md: 4 }} key={item}>
                                    <Paper
                                        elevation={0}
                                        sx={{
                                            p: 3,
                                            borderRadius: 3,
                                            border: "1px solid #E2E8F0",
                                            backgroundColor: "#fff",
                                            height: '100%',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: 2
                                        }}
                                    >
                                        {/* Header Info */}
                                        <Box sx={{ display: 'flex', gap: 2 }}>
                                            <Skeleton variant="circular" width={50} height={50} />
                                            <Box sx={{ flexGrow: 1 }}>
                                                <Skeleton variant="text" width="80%" height={24} />
                                                <Skeleton variant="text" width="50%" height={18} />
                                                <Skeleton variant="text" width="30%" height={14} sx={{ mt: 0.5 }} />
                                            </Box>
                                        </Box>
                                        
                                        <Divider sx={{ my: 0.5 }} />

                                        {/* Matched Jobs */}
                                        <Box>
                                            <Skeleton variant="text" width="45%" height={16} sx={{ mb: 1 }} />
                                            <Box sx={{ display: 'flex', gap: 1 }}>
                                                <Skeleton variant="rectangular" width={100} height={24} sx={{ borderRadius: 1.5 }} />
                                                <Skeleton variant="rectangular" width={80} height={24} sx={{ borderRadius: 1.5 }} />
                                            </Box>
                                        </Box>

                                        {/* Skills */}
                                        <Box sx={{ mt: 'auto', pt: 1 }}>
                                            <Skeleton variant="text" width="35%" height={16} sx={{ mb: 1 }} />
                                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                                <Skeleton variant="rectangular" width={50} height={20} sx={{ borderRadius: 1 }} />
                                                <Skeleton variant="rectangular" width={75} height={20} sx={{ borderRadius: 1 }} />
                                                <Skeleton variant="rectangular" width={60} height={20} sx={{ borderRadius: 1 }} />
                                            </Box>
                                        </Box>
                                    </Paper>
                                </Grid>
                            ))}
                        </Grid>
                    ) : matchedCandidates.length === 0 ? (
                        <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 3, border: '1px solid #E0E0E0', bgcolor: '#fff' }}>
                            <Typography color="text.secondary">No candidate matches found for your active job posts yet.</Typography>
                        </Paper>
                    ) : (
                        <Grid container spacing={3}>
                            {matchedCandidates.map(cand => (
                                <Grid size={{ xs: 12, sm: 6, md: 4 }} key={cand.userId}>
                                    <Paper
                                        elevation={0}
                                        sx={{
                                            p: 3,
                                            borderRadius: 3,
                                            border: "1px solid #E2E8F0",
                                            backgroundColor: "#fff",
                                            height: '100%',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                            cursor: 'pointer',
                                            '&:hover': {
                                                transform: 'translateY(-4px)',
                                                boxShadow: '0 12px 20px rgba(107, 49, 196, 0.08)',
                                                borderColor: '#6B31C4'
                                            }
                                        }}
                                        onClick={() => handleCandidateSelect(cand)}
                                    >
                                        {/* Header Info */}
                                        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                                            <Avatar
                                                src={cand.avatarUrl || ''}
                                                sx={{ width: 50, height: 50, bgcolor: '#6B31C4', fontWeight: 600 }}
                                            >
                                                {cand.name.split(' ').map(n => n[0]).join('').toUpperCase()}
                                            </Avatar>
                                            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                                                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#333', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                    {cand.name}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary" sx={{ textTransform: 'capitalize', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                    {cand.jobTitle || 'No title specified'}
                                                </Typography>
                                                {cand.location && (
                                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                                        📍 {cand.location}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Box>

                                        {/* Matched Job Badges */}
                                        <Box sx={{ mb: 2.5 }}>
                                            <Typography variant="caption" sx={{ fontWeight: 700, color: '#6B31C4', textTransform: 'uppercase', display: 'block', mb: 1 }}>
                                                Matched Jobs:
                                            </Typography>
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                                {cand.matchedJobs.map(job => (
                                                    <Chip
                                                        key={job.id}
                                                        size="small"
                                                        label={`${job.title} (${job.percent}%)`}
                                                        sx={{
                                                            backgroundColor: job.percent >= 70 ? '#e8f5e9' : '#fef3c7',
                                                            color: job.percent >= 70 ? '#2e7d32' : '#d97706',
                                                            fontWeight: 600,
                                                            fontSize: '11px',
                                                            borderRadius: 1.5,
                                                            border: `1px solid ${job.percent >= 70 ? '#c8e6c9' : '#fde68a'}`
                                                        }}
                                                    />
                                                ))}
                                            </Box>
                                        </Box>

                                        <Divider sx={{ my: 1.5 }} />

                                        {/* Skills */}
                                        <Box sx={{ mt: 'auto' }}>
                                            <Typography variant="caption" sx={{ fontWeight: 600, color: '#666', display: 'block', mb: 1 }}>
                                                Skills Match:
                                            </Typography>
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.8 }}>
                                                {cand.skills.slice(0, 4).map((skill, index) => (
                                                    <Chip
                                                        key={index}
                                                        size="small"
                                                        label={typeof skill === 'string' ? skill : skill.name}
                                                        sx={{
                                                            fontSize: '10px',
                                                            height: 20,
                                                            backgroundColor: '#f8fafc',
                                                            color: '#475569',
                                                            border: '1px solid #cbd5e1'
                                                        }}
                                                    />
                                                ))}
                                                {cand.skills.length > 4 && (
                                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', ml: 0.5 }}>
                                                        +{cand.skills.length - 4} more
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Box>
                                    </Paper>
                                </Grid>
                            ))}
                        </Grid>
                    )}
                    </Box>
            </Container>

            <Dialog
                open={Boolean(selectedCandidate)}
                onClose={() => setSelectedCandidate(null)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle sx={{ fontWeight: 700 }}>Candidate Details</DialogTitle>
                <DialogContent dividers>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                        <Avatar
                            src={selectedCandidate?.avatarUrl || ''}
                            sx={{ width: 64, height: 64, bgcolor: '#6B31C4', fontWeight: 700 }}
                        >
                            {selectedCandidate?.name?.split(' ').map(n => n[0]).join('').toUpperCase()}
                        </Avatar>
                        <Box>
                            <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                {selectedCandidate?.name || 'Unnamed Candidate'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {selectedCandidate?.jobTitle || 'Job title not specified'}
                            </Typography>
                            {selectedCandidate?.location && (
                                <Typography variant="body2" color="text.secondary">
                                    📍 {selectedCandidate.location}
                                </Typography>
                            )}
                        </Box>
                    </Box>

                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
                        {selectedCandidate?.experience != null && (
                            <Box>
                                <Typography variant="caption" color="text.secondary">Experience</Typography>
                                <Typography variant="body2">{selectedCandidate.experience}</Typography>
                            </Box>
                        )}
                        {selectedCandidate?.phone && (
                            <Box>
                                <Typography variant="caption" color="text.secondary">Phone</Typography>
                                <Typography variant="body2">{selectedCandidate.phone}</Typography>
                            </Box>
                        )}
                        {selectedCandidate?._raw?.email && (
                            <Box>
                                <Typography variant="caption" color="text.secondary">Email</Typography>
                                <Typography variant="body2">{selectedCandidate._raw.email}</Typography>
                            </Box>
                        )}
                    </Box>

                    {selectedCandidate?.matchedJobs?.length > 0 && (
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                                Matched Jobs
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {selectedCandidate.matchedJobs.map(job => (
                                    <Chip
                                        key={job.id}
                                        size="small"
                                        label={`${job.title} (${job.percent}%)`}
                                        sx={{ backgroundColor: '#eef2ff', color: '#4338ca', fontWeight: 600 }}
                                    />
                                ))}
                            </Box>
                        </Box>
                    )}

                    {selectedCandidate?.skills?.length > 0 && (
                        <Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                                Skills
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {selectedCandidate.skills.map((skill, index) => (
                                    <Chip
                                        key={`${typeof skill === 'string' ? skill : skill.name || skill.skillName || ''}-${index}`}
                                        size="small"
                                        label={typeof skill === 'string' ? skill : skill.name || skill.skillName || ''}
                                        sx={{ backgroundColor: '#f3f4f6', color: '#1f2937' }}
                                    />
                                ))}
                            </Box>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={handleViewResume}
                        variant="outlined"
                        disabled={resumeLoading}
                        sx={{ textTransform: 'none' }}
                    >
                        {resumeLoading ? 'Loading...' : 'View Resume'}
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleDownloadResume}
                        sx={{ textTransform: 'none' }}
                    >
                        Download Resume
                    </Button>
                    <Button onClick={() => setSelectedCandidate(null)} sx={{ textTransform: 'none' }}>
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

        </Box>
    );
};

export default SearchCandidate;
